(function () {
  'use strict';
const cache = new Map();

class PatternKitQuickView extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.dialog = this.querySelector('[data-kit-quick-dialog]');
    this.content = this.querySelector('[data-kit-quick-content]');
    this.state = this.querySelector('[data-kit-quick-state]');
    this.messageEl = this.querySelector('[data-kit-quick-state-message]');
    this.actionsEl = this.querySelector('[data-kit-quick-state-actions]');
    this.fallback = this.querySelector('[data-kit-quick-fallback]');
    this.events = new AbortController();
    const opts = { signal: this.events.signal };
    if (!this.dialog || !this.content) return;

    this.addEventListener('click', (event) => this.onClick(event), opts);
    this.addEventListener('change', (event) => this.onChange(event), opts);
    this.addEventListener('pointerover', (event) => this.onPrefetch(event), opts);
    this.addEventListener('focusin', (event) => this.onPrefetch(event), opts);

    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      opts
    );
    this.dialog.addEventListener('close', () => this.onClose(), opts);

    this.onPageShow = () => {
      if (this.dialog.open) this.close();
    };
    window.addEventListener('pageshow', this.onPageShow, opts);

    document.addEventListener('cart:added', (event) => this.finishCart(event, true), opts);
    document.addEventListener('cart:error', (event) => this.finishCart(event, false), opts);
  }

  disconnectedCallback() {
    this.events?.abort();
  }

  finishCart(event, succeeded) {
    if (!this.pendingId || event.detail?.requestId !== this.pendingId) return;
    this.pendingId = null;
    this.setBusy(false);
    if (succeeded) this.close();
  }

  onClick(event) {
    const trigger = event.target.closest('[data-kit-quick-open]');
    if (trigger && this.contains(trigger)) {
      this.open(trigger);
      return;
    }

    if (event.target.closest('[data-kit-quick-retry]')) {
      if (this.opener) this.open(this.opener, true);
      return;
    }

    if (event.target.closest('[data-kit-card-confirm]')) this.addKit();
  }

  onChange(event) {
    if (event.target.matches('[data-kit-size]') && this.dialog.contains(event.target)) this.update();
  }

  onPrefetch(event) {
    const trigger = event.target.closest('[data-kit-quick-open]');
    if (!trigger || !this.contains(trigger) || trigger.disabled) return;
    if (event.type === 'pointerover' && trigger.contains(event.relatedTarget)) return;

    const url = trigger.dataset.kitQuickUrl;
    if (url) this.load(url).catch(() => {});
  }

  async open(trigger, force = false) {
    if (!trigger || trigger.disabled || typeof this.dialog.showModal !== 'function') return;

    const url = trigger.dataset.kitQuickUrl;
    if (!url) return;

    this.opener = trigger;
    this.active = url;
    this.requestToken = (this.requestToken || 0) + 1;
    const token = this.requestToken;
    if (this.fallback) this.fallback.href = trigger.dataset.kitProductUrl || '#';

    if (force) cache.delete(url);
    this.showState('loading', this.dataset.loadingLabel);
    if (!this.dialog.open) this.dialog.showModal();

    try {
      const markup = await this.load(url);
      if (token !== this.requestToken || this.active !== url || !this.dialog.open) return;
      this.render(markup);
    } catch (error) {
      if (token !== this.requestToken || this.active !== url) return;
      this.showState('error', this.dataset.errorLabel);
    }
  }

  load(url) {
    if (cache.has(url)) return cache.get(url);

    const request = fetch(url, {
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const payload = template.content.querySelector('[data-kit-quick-view-content]');
        if (!payload) throw new Error('Invalid quick view response');
        return payload.outerHTML;
      })
      .catch((error) => {
        cache.delete(url);
        throw error;
      });

    cache.set(url, request);
    return request;
  }

  render(markup) {
    this.content.innerHTML = markup;
    this.content.hidden = false;
    this.state.hidden = true;
    this.dialog.removeAttribute('aria-busy');

    const customLabel = this.dataset.addKitLabel;
    const label = this.content.querySelector('[data-kit-card-confirm-text]');
    if (label && customLabel) label.textContent = customLabel;

    this.update();
    this.size?.focus();
  }

  showState(status, message) {
    this.content.hidden = true;
    this.state.hidden = false;
    this.state.dataset.status = status;
    if (this.messageEl) this.messageEl.textContent = message || '';
    if (this.actionsEl) this.actionsEl.hidden = status !== 'error';
    this.dialog.setAttribute('aria-busy', String(status === 'loading'));
  }

  get size() {
    return this.content.querySelector('[data-kit-size]:checked');
  }

  isSelected(item) {
    return item.dataset.sizeKey === this.size?.value;
  }

  ballText(count) {
    const template = count === 1 ? this.dataset.ballOne : this.dataset.ballOther;
    return String(template || '').replace('__count__', String(count));
  }

  update() {
    const selected = this.size;
    const total = Number(selected?.dataset.totalSkeins) || 0;

    this.content.querySelectorAll('[data-kit-card-item]').forEach((item) => {
      item.hidden = !this.isSelected(item);
    });

    const sizeSummary = this.content.querySelector('[data-kit-card-size-summary]');
    const countSummary = this.content.querySelector('[data-kit-card-count-summary]');
    if (sizeSummary) sizeSummary.textContent = selected?.dataset.sizeLabel || '';
    if (countSummary) countSummary.textContent = this.ballText(total);

    const pattern = this.content.querySelector('[data-kit-card-pattern]');
    const patternOut = pattern?.dataset.available !== 'true';
    let message = patternOut ? this.dataset.patternUnavailable || '' : '';
    const yarnOut = Array.from(this.content.querySelectorAll('[data-kit-card-item]')).some((item) => {
      if (!this.isSelected(item)) return false;

      const needed = Number(item.dataset.quantity) || 0;
      const available = Number(item.dataset.inventoryQuantity) || 0;
      const insufficient =
        item.dataset.inventoryTracked === 'true' &&
        item.dataset.inventoryPolicy !== 'continue' &&
        available < needed;

      if (insufficient && !message) {
        message = String(this.dataset.insufficientStock || '')
          .replace('__needed__', String(needed))
          .replace('__available__', String(Math.max(available, 0)));
      } else if (item.dataset.available !== 'true' && !message) {
        message = this.dataset.soldOut || '';
      }

      return item.dataset.available !== 'true' || insufficient;
    });
    const unavailable = !selected || total <= 0 || patternOut || yarnOut;
    const confirm = this.content.querySelector('[data-kit-card-confirm]');
    const notice = this.content.querySelector('[data-kit-card-notice]');

    if (notice) {
      notice.textContent = message;
      notice.hidden = message === '';
    }

    if (confirm) {
      confirm.disabled = unavailable || this.busy;
      confirm.setAttribute('aria-busy', String(Boolean(this.busy)));
    }
  }

  items() {
    const selected = this.size;
    const pattern = this.content.querySelector('[data-kit-card-pattern]');
    const patternVariantId = Number(pattern?.dataset.variantId) || 0;
    if (!selected || !patternVariantId) return [];

    const kitId = 'kit-quick-' + Date.now();
    const patternProperties = { _kit: kitId };
    const sizeProperty = this.dataset.sizeProperty || '';
    const sizeLabel = selected.dataset.sizeLabel || '';
    if (sizeProperty && sizeLabel) patternProperties[sizeProperty] = sizeLabel;

    const grouped = new Map();
    grouped.set('pattern:' + patternVariantId, {
      id: patternVariantId,
      quantity: 1,
      properties: patternProperties
    });

    this.content.querySelectorAll('[data-kit-card-item]').forEach((item) => {
      if (!this.isSelected(item)) return;
      const variantId = Number(item.dataset.variantId);
      const quantity = Number(item.dataset.quantity) || 0;
      if (!variantId || quantity <= 0) return;

      const key = 'yarn:' + variantId;
      const existing = grouped.get(key);
      if (existing) {
        existing.quantity += quantity;
      } else {
        grouped.set(key, { id: variantId, quantity: quantity, properties: { _kit: kitId } });
      }
    });

    return Array.from(grouped.values());
  }

  addKit() {
    const confirm = this.content.querySelector('[data-kit-card-confirm]');
    if (!confirm || confirm.disabled) return;
    const items = this.items();
    if (!items.length) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.add !== 'function') return;

    this.pendingId = 'quick-kit-cart-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    this.setBusy(true);
    drawer.add({ items: items, requestId: this.pendingId });
  }

  setBusy(busy) {
    this.busy = Boolean(busy);
    this.update();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  onClose() {
    this.requestToken = (this.requestToken || 0) + 1;
    const otherModal = document.querySelector('dialog[open]:not([data-kit-quick-dialog])');
    if (!otherModal && this.opener?.isConnected) this.opener.focus();
  }
}

if (!customElements.get('pattern-kit-quick-view')) {
  customElements.define('pattern-kit-quick-view', PatternKitQuickView);
}

})();
